import type { MembershipPlanId } from '../types/practitionerSignup'

export const foundingOffer = {
  deadline: 'September 30, 2026',
  headline: 'Join before September 30 and get 2 months free',
  subline:
    'Founding members pay no fees for their first two months, then stay at their founding rate — locked in for life.',
}

export interface FoundingBenefit {
  title: string
  description: string
}

export const foundingBenefits: FoundingBenefit[] = [
  {
    title: '2 months free',
    description:
      'No fees. Build your profile and receive referrals from day one.',
  },
  {
    title: 'Rate locked for life',
    description:
      'Your founding rate is permanent — as long as your subscription is active.',
  },
  {
    title: 'Free verification',
    description:
      'A $25 value, covered for founding members. Your credentials verified before you go live — at no cost to you.',
  },
  {
    title: 'Founding Member recognition',
    description:
      'Permanently on your profile — a lasting signal of trust and early commitment.',
  },
]

export const foundingPerks: string[] = [
  'Founding Member recognition on your profile',
  'Named in launch communications',
  'Social feature on Fem Entity channels',
  'Priority access to new features',
  'Early client matching from our waitlist',
  'Direct input into platform development',
]

export interface FoundingPlan {
  /** Sent to the checkout Edge Function; mapped to a Stripe Price server-side. */
  id: MembershipPlanId
  name: string
  seats: string
  price: string
  priceSuffix: string
  note?: string
  features: string[]
  highlighted?: boolean
}

export const foundingPlans: FoundingPlan[] = [
  {
    id: 'anchor',
    name: 'Anchor',
    seats: '1 practitioner seat',
    price: '$39.99',
    priceSuffix: '/mo',
    features: [
      'Practitioner profile with booking tools',
      'Client management & scheduling',
      'Founding Member recognition + social feature',
    ],
  },
  {
    id: 'bloom',
    name: 'Bloom',
    seats: '3–5 practitioner seats',
    price: '$119.99',
    priceSuffix: '/mo',
    note: 'Ideal for group practices or practitioners who collaborate.',
    highlighted: true,
    features: [
      'Shared scheduling & client management across your team',
      'Every team member gets their own profile and visibility',
      'Founding Member recognition for all seats',
    ],
  },
  {
    id: 'thrive',
    name: 'Thrive',
    seats: '6–10 practitioner seats',
    price: '$274.99',
    priceSuffix: '/mo',
    features: [
      'Advanced team management tools',
      'Enhanced visibility & priority support',
      'Early enterprise partnership access',
    ],
  },
  {
    id: 'collective',
    name: 'Collective',
    seats: '10+ practitioner seats',
    price: '$274.99',
    priceSuffix: ' + $24.99/seat',
    features: [
      'Everything in Thrive',
      'Scalable, unlimited seat access',
      'Custom enterprise onboarding',
      'Organization-level founding recognition',
    ],
  },
]

export const foundingCommitmentsIntro =
  'Help shape the platform in a way that works for you — join a feedback session, participate in our Discord community, or complete a feedback survey during your complimentary period.'

export const foundingMembershipMessage =
  'Your first two months are complimentary. Your Founding Member rate begins in month three and remains locked in while your membership remains active.'

export const foundingRateNote =
  'Your Founding Member rate stays locked in for as long as your membership remains active. If you cancel and rejoin later, the rate available at that time will apply.'

export interface FoundingCommitment {
  text: string
  optional?: boolean
}

export const foundingCommitments: FoundingCommitment[] = [
  {
    text: 'Complete your profile within 14 days of onboarding',
  },
  {
    text: 'Attend one virtual onboarding session (30 min)',
  },
  {
    text: 'Participate in one feedback touchpoint during the free period',
  },
  {
    text: 'Maintain an active subscription from Month 3 onward',
    optional: true,
  },
]
